import {
  LightIcon,
  type PlanResponse,
  type PlanValueProp,
} from "@Light/services/lightTypes";

type CreditCopy = {
  headline: string;
  subtext: string;
  optOutCredits: string;
  hasBattery: boolean;
};

const SOLAR_BATTERY_COPY: CreditCopy = {
  headline:
    "We’ve designed the grid energy plan that gets you the most value from your battery and solar system.",
  subtext: "from excess solar energy and battery usage",
  optOutCredits: "battery and solar credits",
  hasBattery: true,
};

const CREDIT_COPY_BY_VALUE_PROP_ID: Record<string, CreditCopy> = {
  solar_battery_credits: SOLAR_BATTERY_COPY,
  solar_battery_credits_fixed: SOLAR_BATTERY_COPY,
  solar_credits_default: {
    headline:
      "We’ve designed the grid energy plan that gets you the most value from your solar system.",
    subtext: "from excess solar energy",
    optOutCredits: "solar credits",
    hasBattery: false,
  },
  battery_credits: {
    headline:
      "We’ve designed the grid energy plan that gets you the most value from your battery system.",
    subtext: "from battery usage",
    optOutCredits: "battery credits",
    hasBattery: true,
  },
};

const DOLLAR_AMOUNT = /\$[\d,]+(?:\.\d+)?/;

export type CreditFocusPresentation = {
  headline: string;
  amount: string;
  subtext: string;
  calloutText?: string;
  valueProps: PlanValueProp[];
  optOut: {
    credits: string;
    description?: string;
  };
};

/**
 * Builds the credit-focused presentation of a rate, where the estimated annual
 * credit is the headline number and the rate becomes the first value prop.
 */
export function getCreditFocusPresentation(
  rate: PlanResponse,
): CreditFocusPresentation | undefined {
  const valueProps = rate.presentation.value_propositions;
  const creditValueProp = valueProps.find(
    (vp) => vp.id in CREDIT_COPY_BY_VALUE_PROP_ID,
  );
  if (!creditValueProp) return undefined;

  const amountCallout = creditValueProp.callouts.find((callout) =>
    DOLLAR_AMOUNT.test(callout.matches),
  );
  const amount = amountCallout?.matches.match(DOLLAR_AMOUNT)?.[0];
  if (!amount) return undefined;

  const copy = CREDIT_COPY_BY_VALUE_PROP_ID[creditValueProp.id];
  return {
    headline: copy.headline,
    amount,
    subtext: copy.subtext,
    calloutText: amountCallout?.text,
    valueProps: [
      rateValueProp(rate),
      ...valueProps.filter((vp) => vp !== creditValueProp),
    ],
    optOut: {
      credits: copy.optOutCredits,
      description: optOutDescription(rate, copy),
    },
  };
}

const optOutDescription = (
  rate: PlanResponse,
  copy: CreditCopy,
): string | undefined => {
  const preactivationPercent = rate.solar_preactivation_credit_percent;
  const sentences = [
    copy.hasBattery &&
      "Other providers won't have the control required of your battery for you to receive battery credits.",
    preactivationPercent > 0 &&
      `You’ll also receive ${preactivationPercent}% off energy until hardware is active.`,
  ].filter(Boolean);
  return sentences.length ? sentences.join(" ") : undefined;
};

const rateValueProp = (rate: PlanResponse): PlanValueProp => {
  const term = rate.term_months > 1 ? ` for ${rate.term_months} months` : "";
  return {
    id: "credit_focused_rate",
    icon: LightIcon.PiggyBank,
    text: `Low average rate of ${rate.avg_cents_per_kwh_1000}¢ per kWh at 1000 kWh${term}`,
    callouts: [],
  };
};
